import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { getCols } from "../../common/getCols.ts";
import { DataTable } from "../components/DataTable/index.tsx";
import type { View } from "../../common/types.ts";
import { frivolitiesMenu } from "./Frivolities.tsx";
import { PHASE_TEXT } from "../../common/constants.ts";
import PickText from "./TradeSummary/PickText.tsx";
import TeamLogoAndName from "../components/TeamLogoAndName.tsx";
import { bySport } from "../../common/sportFunctions.ts";
import { useLocal } from "../util/local.ts";

const PlayerInfo = ({
	asset,
	challengeNoRatings,
}: {
	asset: {
		age: number;
		name: string;
		ovr: number;
		pid: number;
		pos: string;
		pot: number;
		retiredYear: number;
	};
	challengeNoRatings: boolean;
}) => {
	return (
		<>
			<span className="d-inline-block text-end me-1" style={{ minWidth: 19 }}>
				{asset.pos}
			</span>
			<a href={helpers.leagueUrl(["player", asset.pid])}>{asset.name}</a> (
			{!challengeNoRatings || asset.retiredYear !== Infinity
				? `${asset.ovr}/${asset.pot}, `
				: null}
			{asset.age} yo)
		</>
	);
};

const FrivolitiesTrades = ({
	abbrev,
	description,
	title,
	trades,
	type,
}: View<"frivolitiesTrades">) => {
	useTitleBar({
		title,
		customMenu: frivolitiesMenu,
		dropdownView: `frivolities/trades/${type}`,
		dropdownFields: {
			teamsAndAll: abbrev,
		},
	});

	const { challengeNoRatings, userTid } = useLocal([
		"challengeNoRatings",
		"userTid",
	]);

	const cols = getCols([
		"#",
		"Season",
		"Team",
		"Received",
		`stat:${bySport({
			baseball: "war",
			basketball: "ws",
			football: "av",
			hockey: "ps",
		})}`,
		"Team",
		"Received",
		`stat:${bySport({
			baseball: "war",
			basketball: "ws",
			football: "av",
			hockey: "ps",
		})}`,
		"Links",
	]);
	for (const i of [4, 7]) {
		if (cols[i]!.desc) {
			cols[i]!.desc += " (Total After Trade)";
		}
	}

	const superCols = [
		{
			title: "",
			colspan: 2,
		},
		{
			title: "Team 1",
			colspan: 3,
		},
		{
			title: "Team 2",
			colspan: 3,
		},
		{
			title: "",
			colspan: 1,
		},
	];

	const rows = trades.map((trade) => {
		const teamCols = trade.teams.map((t) => {
			const searchValue = t.assets
				.map((asset) => {
					if (asset.type === "player") {
						return `${asset.pos} ${asset.name}`;
					}
					if (asset.type === "deletedPlayer") {
						return asset.name;
					}

					if (asset.type === "realizedPick") {
						return `${asset.pos} ${asset.name} ${helpers.ordinal(
							asset.round,
						)} round pick}`;
					}

					if (asset.type === "unrealizedPick") {
						return `${helpers.ordinal(asset.round)} round pick}`;
					}
				})
				.join(" ");

			const sortValue = t.assets.length;

			return [
				<TeamLogoAndName
					t={{
						...t,
						seasonAttrs: t,
					}}
					url={helpers.leagueUrl([
						"roster",
						`${t.abbrev}_${t.tid}`,
						trade.season,
					])}
					noLogo
				/>,
				{
					value: (
						<ul className="list-unstyled mb-0">
							{t.assets.map((asset, i) => {
								if (asset.type === "player") {
									return (
										<li key={i}>
											<PlayerInfo
												asset={asset}
												challengeNoRatings={challengeNoRatings}
											/>
										</li>
									);
								}

								if (asset.type === "deletedPlayer") {
									return (
										<li key={i}>
											<a href={helpers.leagueUrl(["player", asset.pid])}>
												{asset.name}
											</a>
										</li>
									);
								}

								if (asset.type === "realizedPick") {
									return (
										<li key={i}>
											<PlayerInfo
												asset={asset}
												challengeNoRatings={challengeNoRatings}
											/>
											<br />
											<span
												className="text-body-secondary"
												style={{ paddingLeft: 24 }}
											>
												via <PickText asset={asset} season={trade.season} />
											</span>
										</li>
									);
								}

								if (asset.type === "unrealizedPick") {
									return (
										<li key={i}>
											<PickText asset={asset} season={trade.season} />
										</li>
									);
								}
							})}
						</ul>
					),
					searchValue,
					sortValue,
				},
				helpers.roundStat(t.statSum, "ws"),
			];
		});

		return {
			key: trade.rank,
			data: [
				trade.rank,
				{
					value: `${trade.season} ${PHASE_TEXT[trade.phase]}`,
					sortValue: trade.season + (trade.phase + 10) / 1000,
				},
				...teamCols[0]!,
				...teamCols[1]!,
				<a href={helpers.leagueUrl(["trade_summary", trade.eid])}>Details</a>,
			],
			classNames: {
				"table-info":
					trade.teams[0].tid === userTid || trade.teams[1].tid === userTid,
			},
		};
	});

	return (
		<>
			{description ? <p>{description}</p> : null}

			<DataTable
				className="align-top-all"
				cols={cols}
				defaultSort={[0, "asc"]}
				name={`FrivolitiesTrades_${type}`}
				nonfluid
				rows={rows}
				superCols={superCols}
			/>
		</>
	);
};

export default FrivolitiesTrades;
